import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CheckIcon,
  CircleIcon,
} from '@langchain/untitled-ui-icons';

import { Link } from 'react-router-dom';

import { RunDetailsNavigation } from '@/Pages/Run/components/RunDetailsNavigation';
import { RunTreeSkeleton } from '@/Pages/Run/components/RunTreeSkeleton';
import { Banner } from '@/components/Banner';
import { CopyInlineLink } from '@/components/CopyInlineLink';
import { Button, Spinner, Text } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import { RunSchema } from '@/types/schema';
import {
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

import {
  onboardingSystemMessagePrompt,
  onboardingToolName,
} from '../constants';

export const TracingNextSteps = ({
  projectName,
  lang,
  run,
  sessionId,
  rootRuns,
  childRunLookup,
  hasModifiedSystemMessage,
  hasModifiedTools,
  isLoadingSystemMessageQuery,
  isLoadingToolsQuery,
}: {
  projectName: string;
  lang: 'python' | 'typescript';
  run: RunSchema | undefined;
  sessionId: string | undefined;
  rootRuns: RunSchema[];
  childRunLookup: Record<string, RunSchema[]> | undefined;
  hasModifiedSystemMessage: boolean;
  hasModifiedTools: boolean;
  isLoadingSystemMessageQuery: boolean;
  isLoadingToolsQuery: boolean;
}) => {
  if (!run || !childRunLookup) {
    return (
      <div className="flex h-screen grow items-start gap-10 p-[72px]">
        <div className="flex h-full flex-col items-start gap-6 px-[60px]">
          <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-brand-tertiary p-2">
            <Spinner size="lg" />
          </div>
          <Text
            variant="h1"
            color="primary"
            className="text-5xl"
            weight="normal"
          >
            Tracing your agent...
          </Text>
          <Text variant="h2" color="tertiary" weight="normal">
            In a few seconds you'll be able to see your trace here.
          </Text>
        </div>
        <div className="flex h-full w-full flex-col items-start gap-3">
          <div className="group grid h-[380px] w-full grid-cols-[4fr,3fr] gap-7 rounded-2xl border border-brand-strong px-6 py-7 shadow-lg">
            <div className="w-full overflow-hidden">
              <RunTreeSkeleton hideHeader={true} />
            </div>
            <div className="flex flex-col items-start gap-3">
              <Text variant="h2" color="primary" as="span">
                Waiting for your first trace...
              </Text>
              <Text color="quaternary">
                Keep this tab open while your code is running.
              </Text>
              <Text color="quaternary">
                Once we detect the trace this screen will update.
              </Text>
            </div>
          </div>
          <Link to="/">
            <Button color="secondary" variant="outlined">
              LangSmith Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen grow items-start gap-10 p-[72px]">
      <div className="flex h-full flex-col items-start gap-6 px-[60px]">
        <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-brand-tertiary p-2">
          <CheckIcon className="h-9 w-9 text-brand-green-500" />
        </div>
        <Text variant="h1" color="primary" className="text-5xl" weight="normal">
          Agent traced successfully
        </Text>
        <Text variant="h2" color="tertiary" weight="normal">
          Your setup is complete and you're ready to start exploring
        </Text>
      </div>
      <div className="flex h-full flex-col items-start gap-3">
        <Text variant="h4" color="secondary">
          What's next?
        </Text>
        <NextStepCards
          projectName={projectName}
          lang={lang}
          run={run}
          sessionId={sessionId}
          rootRuns={rootRuns}
          childRunLookup={childRunLookup}
          hasModifiedSystemMessage={hasModifiedSystemMessage}
          hasModifiedTools={hasModifiedTools}
          isLoadingSystemMessageQuery={isLoadingSystemMessageQuery}
          isLoadingToolsQuery={isLoadingToolsQuery}
        />
        {hasModifiedSystemMessage && hasModifiedTools ? (
          <Banner
            title="You've set up tracing successfully!"
            description={
              <div className="flex flex-col gap-2">
                <Text>
                  Your agent is now set up and sending traces. You're ready to
                  start exploring LangSmith.
                </Text>
                <Link to="/">
                  <Button
                    variant="underlined"
                    color="secondary"
                    className="bg-transparent"
                    rightDecorator={ArrowRightIcon}
                  >
                    LangSmith Home
                  </Button>
                </Link>
              </div>
            }
            open={true}
            intent="success"
            className="w-full"
          />
        ) : (
          <Link to="/">
            <Button color="secondary">LangSmith Home</Button>
          </Link>
        )}
      </div>
    </div>
  );
};

const NextStepCards = ({
  projectName,
  lang,
  run,
  sessionId,
  rootRuns,
  childRunLookup,
  hasModifiedSystemMessage,
  hasModifiedTools,
  isLoadingSystemMessageQuery,
  isLoadingToolsQuery,
}: {
  projectName: string;
  lang: 'python' | 'typescript';
  run: RunSchema;
  sessionId: string | undefined;
  rootRuns: RunSchema[];
  childRunLookup: Record<string, RunSchema[]>;
  hasModifiedSystemMessage: boolean;
  hasModifiedTools: boolean;
  isLoadingSystemMessageQuery: boolean;
  isLoadingToolsQuery: boolean;
}) => {
  const organizationId = useOrganizationId();
  return (
    <div className="flex w-full flex-col gap-3">
      <a
        href={`/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}?peeked_trace=${run.trace_id}&peek=${run.trace_id}`}
        target="_blank"
      >
        <div className="group grid h-[380px] grid-cols-[4fr,3fr] gap-7 rounded-2xl border border-brand-strong px-6 py-7 shadow-lg hover:bg-secondary">
          <div className="h-full">
            <RunDetailsNavigation
              isPublicPage={false}
              hideHeader={true}
              hideFooter={true}
              run={run}
              rootRuns={rootRuns}
              searchFilter={undefined}
              onSearchFilterChange={() => {}}
              filteredRuns={[]}
              childRunLookup={childRunLookup}
              onNavigate={() => {}}
              insidePane={false}
              togglePanelSize={() => {}}
              comparePosition={undefined}
              showAllRuns={false}
              userPrefs={{
                filteredOnly: false,
                filterCount: 0,
                userAllRuns: false,
                showGraphStats: false,
                setUserFilteredOnly: () => {},
                setUserAllRuns: () => {},
                userFilteredOnly: false,
                setShowGraphStats: () => {},
              }}
              toggleGraphStats={() => {}}
              isXsPanel={false}
            />
          </div>
          <div className="flex flex-col items-start gap-3">
            <Text variant="h2" color="primary" as="span">
              View traces
            </Text>
            <Text color="quaternary">
              Traces are now being sent to the{' '}
              <Text
                color="primary"
                as="span"
                weight="medium"
                className="font-mono tracking-tight"
              >
                {projectName}{' '}
              </Text>
              tracing project. You can inspect them to follow how your agent
              processes each request.
            </Text>
            <div className="ml-auto mt-auto flex items-center gap-1 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1">
              <Text
                variant="md"
                className="text-brand-green-900 opacity-0 duration-300 ease-in-out group-hover:opacity-100"
              >
                View trace
              </Text>
              <ArrowUpRightIcon className="h-14 w-14 font-medium [&_path]:stroke-[0.3px]" />
            </div>
          </div>
        </div>
      </a>
      <div className="rounded-2xl border border-secondary px-6 py-7">
        <div className="flex items-start gap-2">
          {hasModifiedSystemMessage ? (
            <div className="h-6 w-6 shrink-0 rounded-full bg-brand-primary ">
              <CheckIcon className="p-[3px] text-white" />
            </div>
          ) : isLoadingSystemMessageQuery ? (
            <Spinner size="md" />
          ) : (
            <CircleIcon className="size-6 shrink-0 text-brand-secondary [&>path]:stroke-1" />
          )}
          <div className="flex w-full flex-col gap-2">
            <Text variant="h2" color="primary" as="span">
              Try changing the prompt
            </Text>
            <Text color="quaternary" as="span">
              <span>Try changing the prompt to </span>
              <CopyInlineLink
                value={onboardingSystemMessagePrompt}
                className="inline-flex w-fit"
                prefix="Prompt"
                hideIcon
              >
                <Text
                  color="primary"
                  weight="medium"
                  className="pt-0.5 font-mono tracking-tight"
                >
                  {onboardingSystemMessagePrompt}
                </Text>
              </CopyInlineLink>{' '}
              <span>and see how it responds.</span>
            </Text>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-secondary px-6 py-7">
        <div className="flex items-start gap-2">
          {hasModifiedTools ? (
            <div className="h-6 w-6 shrink-0 rounded-full bg-brand-primary ">
              <CheckIcon className="p-[3px] text-white" />
            </div>
          ) : isLoadingToolsQuery ? (
            <Spinner size="md" />
          ) : (
            <CircleIcon className="size-6 shrink-0 text-brand-secondary [&>path]:stroke-1" />
          )}
          <div className="flex flex-col gap-2">
            <Text variant="h2" color="primary" as="span">
              Add another tool
            </Text>
            <Text color="quaternary" as="span">
              <span>Add the tool</span>{' '}
              <CopyInlineLink
                value={onboardingToolName(lang)}
                prefix="Tool"
                className="inline-flex w-fit"
                hideIcon
              >
                <Text
                  color="primary"
                  weight="medium"
                  className="pt-0.5 font-mono tracking-tight"
                >
                  {onboardingToolName(lang)}
                </Text>
              </CopyInlineLink>{' '}
              <span>and test how the agent decides which tool to use.</span>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};
